/**
 * EditViewToggle - Button-based tab toggle (lesson-mcq-edit-view-toggle style)
 * Content types use this to render Content | Questions | Configuration etc.
 */

import React from 'react';
import { Button } from '@ai-content-hub/content-kit';
import type { EditTabDescriptor } from '../schema/content';
import { cn } from '../lib/utils';

export interface EditViewToggleProps {
    /** Tab descriptors */
    tabs: EditTabDescriptor[];
    /** Active tab id */
    activeTab: string;
    /** Tab change handler */
    onTabChange: (tabId: string) => void;
    /** Optional root class (e.g. essay-edit-view-toggle, lesson-mcq-edit-view-toggle) */
    className?: string;
    /** Optional button class prefix for BEM modifiers */
    buttonClassPrefix?: string;
}

export function EditViewToggle({
    tabs,
    activeTab,
    onTabChange,
    className = 'content-editor-view-toggle',
    buttonClassPrefix = 'content-editor-view-toggle-button',
}: EditViewToggleProps): React.ReactElement {
    return (
        <div className={cn(className, 'flex items-center gap-2 mb-4')}>
            {tabs.map((tab) => {
                const Icon = tab.icon;
                const label = tab.badge !== undefined ? `${tab.label} (${tab.badge})` : tab.label;
                return (
                    <Button
                        key={tab.id}
                        variant={activeTab === tab.id ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => onTabChange(tab.id)}
                        className={cn(buttonClassPrefix, `${buttonClassPrefix}--${tab.id}`)}
                    >
                        {Icon && <Icon className="h-4 w-4 mr-2" />}
                        {label}
                    </Button>
                );
            })}
        </div>
    );
}
